import React, { FC } from "react";
import classNames from "classnames";
import { Fade } from "react-awesome-reveal";
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import { useTranslation, Trans } from "react-i18next";
import loadable from "@loadable/component";
import { ApplePayLogo } from "@app/components/common/icons";
import { usePlayIcon } from "@app/hooks/use-play-icon/usePlayIcon";
import { DEFAULT_FADE_DURATION } from "@app/constants";
import placeholder from "./placeholder.webp";

type Step = {
  title: string;
  body: string;
};

const ReactPlayer = loadable(() => import("react-player"));

export const HowToPay: FC = () => {
  const { t } = useTranslation("p_bank_apple-pay");
  const playIcon = usePlayIcon();

  return (
    <div
      className={classNames(
        "mt-20 flex flex-col rounded-lg border border-divider",
        "md:flex-row md:gap-10 md:rounded-sm md:py-15 md:pl-10 md:pr-10",
        "lg:mt-25 lg:py-25 lg:pl-20",
        "xl:mt-40 xl:max-w-container xl:flex-col xl:items-center xl:gap-40 xl:rounded-none xl:border-0 xl:px-0 xl:py-0",
      )}
    >
      <Fade
        duration={DEFAULT_FADE_DURATION}
        triggerOnce
        className={classNames(
          "w-full",
          "md:first:order-last",
          "lg:max-w-[66%]",
        )}
        cascade
      >
        <div
          className={classNames(
            "aspect-video h-fit w-full overflow-hidden rounded-t-lg",
            "md:flex-1 md:rounded-none",
            "lg:pl-10",
            "xl:flex-none xl:rounded-lg xl:pl-0",
          )}
        >
          <ReactPlayer
            url="https://www.youtube.com/watch?v=V7eh_xe5SXQ"
            width="100%"
            height="100%"
            playIcon={
              <GatsbyImage
                image={getImage(playIcon)}
                alt="Play"
                className="h-15 w-15"
              />
            }
            light={placeholder}
            controls
            playing
            style={{ objectFit: "cover" }}
          />
        </div>
        <div
          className={classNames(
            "px-9 py-5",
            "md:flex-1 md:px-0 md:py-0",
            "xl:flex-none",
          )}
        >
          <div className="xl:text-center">
            <h2
              className={classNames(
                "mb-2.5 text-medium text-primary-content",
                "lg:mb-5 lg:text-large",
                "xl:text-xx-large",
              )}
            >
              {t("how_to_pay_title")}
            </h2>
            <p
              className={classNames(
                "mb-10 text-regular text-base-400",
                "xl:mb-40",
              )}
            >
              {t("how_to_pay_paragraph")}
            </p>
          </div>
          <div className={classNames("flex flex-col gap-10", "xl:gap-0")}>
            {(t("pay_steps", { returnObjects: true }) as Step[]).map((step) => (
              <div
                key={step.title}
                className={classNames(
                  "xl:flex xl:flex-row xl:justify-between xl:border-t xl:border-divider xl:py-12",
                  "last:xl:border-b last:xl:border-divider",
                )}
              >
                <h3
                  className={classNames(
                    "mb-10 text-regular text-primary-content",
                    "md:mb-2.5",
                    "lg:text-medium",
                    "xl:mb-0 xl:w-[38%] xl:text-large",
                  )}
                >
                  {step.title}
                </h3>
                <p
                  className={classNames(
                    "text-regular text-base-400",
                    "xl:w-[48%] xl:text-medium",
                  )}
                >
                  <Trans
                    t={t}
                    components={[
                      <ApplePayLogo
                        key="apple-pay-logo"
                        className="mx-1 inline-block h-4 w-6 align-baseline"
                      />,
                    ]}
                  >
                    {step.body}
                  </Trans>
                </p>
              </div>
            ))}
          </div>
        </div>
      </Fade>
    </div>
  );
};
